import { useState, useEffect, Suspense } from 'react';

import { useBusinessEntity } from '@zenbusiness/application-commons-business-entity';
import { gql, useMutation } from '@zenbusiness/application-commons-graphql';
import { useIdentity } from '@zenbusiness/application-commons-identity';
import { Navigate, useNavigate, useParams } from 'react-router-dom';

import LinearProgress from '@mui/material/LinearProgress';

import LessonComplete from '../LessonComplete';
import LessonViewerHeader from '../LessonViewerHeader';
import ProgressBar from '../ProgressBar';

import { getLessonData } from '../../cms/index';
import { useLessonValues } from '../../contexts/LessonContext';
import useLessonsQuery from '../../hooks/useLessonsQuery';
import {
  LessonViewerCompletionContainer,
  LessonViewerContentContainer,
  LessonViewerHeaderContainer,
  LessonViewerMainContainer,
  LessonViewerProgressBarContainer, StickyHeader
} from './component.styles';
import { getCustomPage } from './customPages';

/*
  NOTE ON URLS (EDU-124): We are not updating naming conventions in backend, URLS, data, etc,
  to match the new lessons titles, because the urls are the way to identify each lesson,
  and updating the urls would require updating existing data records that contain the progress
  of the users.
*/

const lessonsData = getLessonData();

export const EDUCATION_STUDIO_COMPLETE_ARTICLE = gql`
  mutation CompleteArticle ($accountUuid: UUID, $lesson: String!, $article: String!) {
    completeArticle(
      accountUuid: $accountUuid
      lesson: $lesson
      article: $article
    ) {
      success
      errorMessage
    }
  }
`;

window.NativeApp = window.NativeApp || {
  articleDidLoad() { },
  articleWillLoad() { },
  closeLesson() { }
};

const LessonViewer = () => {
  const { accountUuid } = useIdentity();
  const navigate = useNavigate();
  const { businessEntityUuid } = useBusinessEntity();
  const { lesson, step } = useParams();
  const { setActiveLesson } = useLessonValues();
  const [isAllowedArticleCheck, setIsAllowedArticleCheck] = useState(false);

  useEffect(() => {
    setActiveLesson(lesson);
  }, [setActiveLesson, lesson]);

  const [isSaving, setIsSaving] = useState(false);
  const [currentLesson, setCurrentLesson] = useState({
    ...lessonsData.find((lessonValue) => lessonValue.url === lesson),
    currentStep: 0,
    progress: 0
  });

  // GQL Mutation
  const [markArticleAsComplete] = useMutation(EDUCATION_STUDIO_COMPLETE_ARTICLE, {
    variables: {
      accountUuid,
      lesson,
      article: step
    }
  });

  const { data } = useLessonsQuery();

  const allLessons = data?.lessons;
  // const [hasWatchedVideo, setHasWatchedVideo] = useState(currentLesson.currentStep !== 0);

  useEffect(() => {
    if (
      currentLesson.url !== lesson
      || !currentLesson.steps.some((stepValue) => stepValue.url === step)
    ) {
      setCurrentLesson({ ...currentLesson, currentStep: 0 });
      navigate(
        `/d/company/${businessEntityUuid}/education-studio/lessons/${currentLesson.url}/${currentLesson.steps[0].url}`
      );
    }
  }, [businessEntityUuid, currentLesson, navigate, lesson, step]);

  const getCurrentStepIndex = () => currentLesson.steps.reduce(
    (total, value, index) => (step === value.url ? total + index : total),
    0
  );

  const getCurrentLessonIndex = () => lessonsData.reduce(
    (total, value, index) => (lesson === value.url ? total + index : total),
    0
  );

  useEffect(() => {
    if (data && !isAllowedArticleCheck) {
      const currentLessonProgress = data?.lessons.find(
        (lessonProgress) => lessonProgress.id === lesson
      );
      if (currentLessonProgress?.articles[getCurrentStepIndex() - 1]?.isCompleted === false) {
        navigate(`/d/company/${businessEntityUuid}/education-studio`);
      }
      setIsAllowedArticleCheck(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  useEffect(() => {
    const currLessonCompletionData = data?.lessons?.find(
      (lcd) => lcd.id === currentLesson.url
    );
    const progress = (currLessonCompletionData?.articles?.findLastIndex(
      (article) => article.isCompleted
    ) ?? 0) + 1;

    if (progress > currentLesson.progress) {
      setCurrentLesson({ ...currentLesson, progress });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  useEffect(() => {
    // To sync currentStep after a page reload
    if (currentLesson.steps.some((stepValue) => stepValue.url === step)) {
      setCurrentLesson({
        ...currentLesson,
        currentStep: getCurrentStepIndex(),
        progress: Math.max(getCurrentStepIndex(), currentLesson.progress)
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  useEffect(() => {
    if (isSaving) {
      setTimeout(() => {
        setIsSaving(false);
      }, 2000);
    }
  }, [isSaving]);

  // communicate with native app via JS bridge
  useEffect(() => {
    const lessonName = currentLesson.name;
    const articleTitle = (
      (currentLesson.currentStep > 0) ? (currentLesson.steps[currentLesson.currentStep].title)
        : currentLesson.steps[currentLesson.currentStep].description
    );
    const articleNumber = currentLesson.currentStep + 1;
    const totalArticles = currentLesson.steps.length;
    const completedArticles = currentLesson.progress;
    const isLessonComplete = currentLesson.progress === currentLesson.steps.length;

    window.NativeApp.articleWillLoad(
      currentLesson.url,
      lessonName,
      articleTitle,
      articleNumber,
      completedArticles,
      totalArticles,
      isLessonComplete
    );
  }, [currentLesson]);

  const goToPrevStep = () => {
    if (currentLesson.currentStep > 0) {
      setCurrentLesson({
        ...currentLesson,
        currentStep: getCurrentStepIndex() - 1
      });
      navigate(
        `/d/company/${businessEntityUuid}/education-studio/lessons/${currentLesson.url
        }/${currentLesson.steps[getCurrentStepIndex() - 1].url}`
      );
    }
  };

  const goToNextStep = async () => {
    // This should load all meta data for the current lesson from the back-end
    if (allLessons) {
      allLessons.filter((item) => item.id === currentLesson.url);
    }

    markArticleAsComplete();
    if (step !== currentLesson.steps[currentLesson.steps.length - 1].url) {
      // last clic in Next Step should redirect to the animation "successfully completed"
      const progress = Math.max(
        getCurrentStepIndex() + 1,
        currentLesson.progress
      );
      setIsSaving(true);
      setCurrentLesson({
        ...currentLesson,
        progress,
        currentStep: getCurrentStepIndex() + 1
      });
      navigate(
        `/d/company/${businessEntityUuid}/education-studio/lessons/${currentLesson.url
        }/${currentLesson.steps[getCurrentStepIndex() + 1].url}`
      );
    }
  };

  const goTo = (stepClicked) => {
    setCurrentLesson({ ...currentLesson, currentStep: stepClicked });
    navigate(
      `/d/company/${businessEntityUuid}/education-studio/lessons/${currentLesson.url}/${currentLesson.steps[stepClicked].url}`
    );
  };

  const goToLessonHub = () => {
    const isLessonComplete = currentLesson.progress === currentLesson.steps.length;
    window.NativeApp.closeLesson(currentLesson.url, isLessonComplete);
    navigate(`/d/company/${businessEntityUuid}/education-studio`);
  };

  const onLessonDone = () => {
    markArticleAsComplete();
    goToLessonHub();
  };

  // TODO: handle state to change of lesson
  const nextLesson = () => {
    markArticleAsComplete();
    const currentLessonIndex = getCurrentLessonIndex();
    const nextLessonIndex = currentLessonIndex === lessonsData.length - 1
      ? 0
      : currentLessonIndex + 1;

    if (nextLessonIndex === 0) {
      goToLessonHub();
    } else {
      setCurrentLesson({
        ...lessonsData[nextLessonIndex],
        currentStep: 0,
        progress: 0
      });
      navigate(
        `/d/company/${businessEntityUuid}/education-studio/lessons/${lessonsData[nextLessonIndex].url}/preview`
      );
    }
  };

  const goBack = currentLesson.steps[currentLesson.currentStep]?.url === 'preview'
    ? goToLessonHub
    : goToPrevStep;

  if (!lesson || !step) {
    return <Navigate to={`/d/company/${businessEntityUuid}`} />;
  }

  if (!isAllowedArticleCheck) {
    return <></>;
  }

  return (
    <>
      {step !== 'complete' ? (
        <LessonViewerMainContainer container alignItems="flex-start">
          <StickyHeader container>
            <LessonViewerHeaderContainer container item>
              <LessonViewerHeader
                title={currentLesson.name}
                backButtonClick={goBack}
                closeButtonClick={goToLessonHub}
                isSaving={isSaving}
              />
            </LessonViewerHeaderContainer>
            <LessonViewerProgressBarContainer container item>
              <ProgressBar
                steps={currentLesson.steps.length - 1}
                progress={currentLesson.progress + 1}
                current={currentLesson.currentStep}
                onClick={goTo}
              />
            </LessonViewerProgressBarContainer>
          </StickyHeader>
          <LessonViewerContentContainer container item>
            <Suspense fallback={<LinearProgress />}>
              {
                getCustomPage(lesson, step, goToNextStep)
              }
            </Suspense>
          </LessonViewerContentContainer>
        </LessonViewerMainContainer>
      ) : (
        <LessonViewerCompletionContainer>
          <LessonComplete
            onPrimaryClick={onLessonDone}
            onSecondaryClick={nextLesson}
          />
        </LessonViewerCompletionContainer>
      )}
    </>
  );
};

export default LessonViewer;
